/**
 * SupplierGuard — lets a signed-in SUPPLIER into the supplier portal.
 *
 * Who is a supplier is the API's answer (`GET /supplier-portal/me`), not a role
 * string read in the browser: the session says which supplier the user belongs
 * to, and every endpoint behind it is scoped by that. Anyone else is steered to
 * their own home.
 *
 * Like the other portal guards it only ever REDIRECTS — it never signs out,
 * because the portals share one session.
 *
 * - A staff or client user (session FORBIDDEN / NOT_FOUND) goes to /design-requests.
 * - A supplier login (access_level 4) the API refuses — not linked to a live
 *   supplier yet — gets a plain message and Sign out instead. Redirecting them
 *   would loop: `SupplierFence` sends every other path straight back here.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isSupplierAccessLevel } from "@/lib/auth/supplierAccess";
/* Deep imports on purpose: the guard is eager, and the portal index pulls in every page. */
import { errorCode } from "@/modules/SupplierPortal/api/portalErrors.ts";
import { useSupplierSession } from "@/modules/SupplierPortal/api/usePortalQueries.ts";

export const SupplierGuard = ({ children }: { children: ReactNode }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const session = useSupplierSession(Boolean(user));

  if (loading || (user && session.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  if (!user) {
    /* Straight to /signin: the legacy /auth redirect would drop `state`, and with it the deep link. */
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search + location.hash }} />;
  }

  if (session.isError || !session.data) {
    const code = errorCode(session.error);
    const refused = code === "FORBIDDEN" || code === "NOT_FOUND";
    if (refused && isSupplierAccessLevel(user.access_level)) {
      return (
        <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center">
          <p className="text-base font-semibold text-foreground">This account is not linked to a supplier yet.</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Ask your Habits365Greek contact to link it, then sign in again.
          </p>
          <Button variant="outline" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>
      );
    }
    /* Not a supplier account: back to the staff portal, never a sign-out. */
    if (refused) return <Navigate to="/design-requests" replace />;
    return (
      <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-2 bg-background p-6 text-center">
        <p className="text-base font-semibold text-foreground">We could not open your supplier account.</p>
        <p className="text-sm text-muted-foreground">Refresh the page, or contact your Habits365Greek contact.</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default SupplierGuard;
